'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { useLanguage } from '@/contexts/LanguageContext';

interface FormData {
  name: string;
  email: string;
  phone: string;
  inquiryType: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
  inquiryType?: string;
  message?: string;
}

const ContactForm = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    inquiryType: '',
    message: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const getInquiryTypes = () => [
    { value: '', label: t('اختر نوع الاستفسار', 'Select Inquiry Type') },
    { value: 'subscription', label: t('استفسار عن الاشتراكات', 'Subscription Inquiry') },
    { value: 'partnership', label: t('طلب شراكة', 'Partnership Request') },
    { value: 'support', label: t('الدعم الفني', 'Technical Support') },
    { value: 'complaint', label: t('شكوى أو اقتراح', 'Complaint or Suggestion') },
    { value: 'other', label: t('أخرى', 'Other') },
  ];

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t('الاسم مطلوب', 'Name is required');
    }

    if (!formData.email.trim()) {
      newErrors.email = t('البريد الإلكتروني مطلوب', 'Email is required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('البريد الإلكتروني غير صحيح', 'Invalid email address');
    }

    if (!formData.phone.trim()) {
      newErrors.phone = t('رقم الهاتف مطلوب', 'Phone number is required');
    } else if (!/^[+]?[0-9]{10,15}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = t('رقم الهاتف غير صحيح', 'Invalid phone number');
    }

    if (!formData.inquiryType) {
      newErrors.inquiryType = t('نوع الاستفسار مطلوب', 'Inquiry type is required');
    }

    if (!formData.message.trim()) {
      newErrors.message = t('الرسالة مطلوبة', 'Message is required');
    } else if (formData.message.trim().length < 10) {
      newErrors.message = t('الرسالة يجب أن تكون 10 أحرف على الأقل', 'Message must be at least 10 characters');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    const inquiryTypes = getInquiryTypes();
    const whatsappMessage = `${t('مرحباً، أنا', 'Hello, I am')} ${formData.name}%0A%0A${t('نوع الاستفسار:', 'Inquiry Type:')} ${inquiryTypes.find(t => t.value === formData.inquiryType)?.label}%0A%0A${t('الرسالة:', 'Message:')}%0A${formData.message}%0A%0A${t('البريد الإلكتروني:', 'Email:')} ${formData.email}%0A${t('رقم الهاتف:', 'Phone:')} ${formData.phone}`;
    
    window.open(`https://wa.me/966590317360?text=${whatsappMessage}`, '_blank');

    setIsSubmitting(false);
    setSubmitSuccess(true);
    setFormData({
      name: '',
      email: '',
      phone: '',
      inquiryType: '',
      message: '',
    });

    setTimeout(() => {
      setSubmitSuccess(false);
    }, 5000);
  };

  if (!isHydrated) {
    return (
      <div className="bg-card rounded-lg p-8 border border-border">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-muted rounded w-1/3"></div>
          <div className="space-y-4">
            <div className="h-12 bg-muted rounded"></div>
            <div className="h-12 bg-muted rounded"></div>
            <div className="h-12 bg-muted rounded"></div>
            <div className="h-12 bg-muted rounded"></div>
            <div className="h-32 bg-muted rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  const inquiryTypes = getInquiryTypes();

  return (
    <div className="bg-card rounded-lg p-8 border border-border shadow-sm">
      <h2 className="text-2xl font-bold text-foreground font-amiri mb-6">
        {t('أرسل لنا رسالة', 'Send Us a Message')}
      </h2>

      {submitSuccess && (
        <div className="mb-6 p-4 bg-success/10 border border-success/20 rounded-lg flex items-start gap-3">
          <Icon name="CheckCircleIcon" size={24} className="text-success flex-shrink-0" />
          <div>
            <p className="text-success font-medium">{t('تم إرسال رسالتك بنجاح!', 'Your message was sent successfully!')}</p>
            <p className="text-sm text-muted-foreground mt-1">
              {t('سنتواصل معك في أقرب وقت ممكن', 'We will contact you as soon as possible')}
            </p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Name Field */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-foreground mb-2">
            {t('الاسم الكامل', 'Full Name')} <span className="text-error">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={`w-full px-4 py-3 rounded-md border ${errors.name ? 'border-error' : 'border-border'} bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-premium`}
            placeholder={t('أدخل اسمك الكامل', 'Enter your full name')}
          />
          {errors.name && (
            <p className="mt-1 text-sm text-error flex items-center gap-1">
              <Icon name="ExclamationCircleIcon" size={16} />
              {errors.name}
            </p>
          )}
        </div>

        {/* Email Field */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
            {t('البريد الإلكتروني', 'Email Address')} <span className="text-error">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={`w-full px-4 py-3 rounded-md border ${errors.email ? 'border-error' : 'border-border'} bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-premium`}
            placeholder="example@email.com"
          />
          {errors.email && (
            <p className="mt-1 text-sm text-error flex items-center gap-1">
              <Icon name="ExclamationCircleIcon" size={16} />
              {errors.email}
            </p>
          )}
        </div>

        {/* Phone Field */}
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-foreground mb-2">
            {t('رقم الهاتف', 'Phone Number')} <span className="text-error">*</span>
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className={`w-full px-4 py-3 rounded-md border ${errors.phone ? 'border-error' : 'border-border'} bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-premium`}
            placeholder="+966 50 123 4567"
          />
          {errors.phone && (
            <p className="mt-1 text-sm text-error flex items-center gap-1">
              <Icon name="ExclamationCircleIcon" size={16} />
              {errors.phone}
            </p>
          )}
        </div>

        {/* Inquiry Type Field */}
        <div>
          <label htmlFor="inquiryType" className="block text-sm font-medium text-foreground mb-2">
            {t('نوع الاستفسار', 'Inquiry Type')} <span className="text-error">*</span>
          </label>
          <select
            id="inquiryType"
            name="inquiryType"
            value={formData.inquiryType}
            onChange={handleChange}
            className={`w-full px-4 py-3 rounded-md border ${errors.inquiryType ? 'border-error' : 'border-border'} bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-premium`}
          >
            {inquiryTypes.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
          {errors.inquiryType && (
            <p className="mt-1 text-sm text-error flex items-center gap-1">
              <Icon name="ExclamationCircleIcon" size={16} />
              {errors.inquiryType}
            </p>
          )}
        </div>

        {/* Message Field */}
        <div>
          <label htmlFor="message" className="block text-sm font-medium text-foreground mb-2">
            {t('الرسالة', 'Message')} <span className="text-error">*</span>
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows={6}
            className={`w-full px-4 py-3 rounded-md border ${errors.message ? 'border-error' : 'border-border'} bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-premium resize-none`}
            placeholder={t('اكتب رسالتك هنا...', 'Write your message here...')}
          />
          {errors.message && (
            <p className="mt-1 text-sm text-error flex items-center gap-1">
              <Icon name="ExclamationCircleIcon" size={16} />
              {errors.message}
            </p>
          )}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-md bg-accent text-accent-foreground font-medium text-lg transition-premium hover:bg-accent/90 hover:shadow-md active-press disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
              <span>{t('جاري الإرسال...', 'Sending...')}</span>
            </>
          ) : (
            <>
              <Icon name="PaperAirplaneIcon" size={20} />
              <span>{t('إرسال الرسالة', 'Send Message')}</span>
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default ContactForm;